import styled, { css, keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(82, 196, 26, 0.4);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(82, 196, 26, 0);
  }
`;

export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: ${({ theme }) => theme.colors.white};
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.black3};
  animation: ${fadeIn} 0.3s ease-out;
`;

export const QuestionText = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black9};
  margin: 0;
  line-height: 1.5;
`;

export const OptionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

interface OptionButtonProps {
  $isSelected?: boolean;
  $isCorrect?: boolean;
  $isIncorrect?: boolean;
  $showResult?: boolean;
}

export const OptionButton = styled.button<OptionButtonProps>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  border: 2px solid ${({ theme }) => theme.colors.black3};
  background: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.green6};
    background: ${({ theme }) => theme.colors.green1};
  }

  ${({ $isSelected, theme }) =>
    $isSelected &&
    css`
      border-color: ${theme.colors.green6};
      background: ${theme.colors.green1};
    `}

  ${({ $showResult, $isCorrect, theme }) =>
    $showResult &&
    $isCorrect &&
    css`
      border-color: ${theme.colors.green6};
      background: ${theme.colors.green1};
      animation: ${pulse} 1s ease-in-out;
    `}

  ${({ $showResult, $isIncorrect, theme }) =>
    $showResult &&
    $isIncorrect &&
    css`
      border-color: ${theme.colors.red6};
      background: ${theme.colors.red1};
    `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

export const OptionLetter = styled.span<{ $isCorrect?: boolean; $isIncorrect?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.black2};
  color: ${({ theme }) => theme.colors.black7};
  font-weight: 600;
  font-size: 13px;
  flex-shrink: 0;

  ${({ $isCorrect, theme }) =>
    $isCorrect &&
    css`
      background: ${theme.colors.green6};
      color: ${theme.colors.white};
    `}

  ${({ $isIncorrect, theme }) =>
    $isIncorrect &&
    css`
      background: ${theme.colors.red6};
      color: ${theme.colors.white};
    `}
`;

export const OptionText = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.black8};
  flex: 1;
`;

export const ResultWrapper = styled.div<{ $isCorrect: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: ${({ $isCorrect, theme }) => ($isCorrect ? theme.colors.green1 : theme.colors.red1)};
  margin-top: 4px;
`;

export const ResultIcon = styled.span<{ $isCorrect: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: ${({ $isCorrect, theme }) => ($isCorrect ? theme.colors.green6 : theme.colors.red6)};
  color: white;
  font-size: 12px;
`;

export const ResultText = styled.span<{ $isCorrect: boolean }>`
  font-size: 13px;
  font-weight: 500;
  color: ${({ $isCorrect, theme }) => ($isCorrect ? theme.colors.green7 : theme.colors.red7)};
`;

export const VoiceHint = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black5};
  margin: 0;
  text-align: center;
  font-style: italic;
`;
